'use client';

import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { color, spacing, fontWeight, breakpoints } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import { TabType } from './types';

const TabsContainer = styled(Box)`
    display: flex;
    width: 100%;
    max-width: 600px;
    justify-content: center;
    margin: 0 auto;
    padding: 0 ${spacing[24]};
    @media (min-width: ${breakpoints.sm}) {
        padding: 0;
    }
`;

const TabInnerBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${spacing[8]};
    background-color: ${props => props.isSelected ? color.brand[60] : color.brand[50]};
    color: ${color.white[0]};
    height: 46px;
    width: 100%;
    transition: background-color 200ms ease, color 200ms ease;
    box-sizing: border-box;
    @media (max-width: 499px) {
        height: 54px;
    }
`;

const TabButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>`
    display: flex;
    flex: 1 1 0;
    align-items: stretch;
    justify-content: stretch;
    padding: ${spacing[16]};
    background-color: ${props => props.selected ? color.gray[60] : 'transparent'};
    color: ${color.white[0]};
    border: none;
    border-radius: 0;
    cursor: pointer;
    width: 100%;
    text-align: center;
    transition: background-color 200ms ease;

    &:focus, &:active {
        outline: none;
            outline-offset: 0;
            box-shadow: none;
        }

    &:focus-visible {
        outline: none;
        outline-offset: 0;
        box-shadow: none;
    }
`;

interface LocationTabsProps {
    activeTab: TabType;
    onTabChange: (tab: TabType) => void;
}

export default function LocationTabs({ activeTab, onTabChange }: LocationTabsProps) {
    const t = useTranslations('HomePage.locationsMap');
    const firstTabRef = useRef<HTMLButtonElement | null>(null);
    const secondTabRef = useRef<HTMLButtonElement | null>(null);

    const handleTabClick = (tab: TabType) => {
        onTabChange(tab);
    };

    const handleTabsKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const next = activeTab === 'us-offices' ? 'brokers' : 'us-offices';
            onTabChange(next);
            if (next === 'us-offices') {
                firstTabRef.current?.focus();
            } else {
                secondTabRef.current?.focus();
            }
        } else if (e.key === 'Home') {
            e.preventDefault();
            onTabChange('us-offices');
            firstTabRef.current?.focus();
        } else if (e.key === 'End') {
            e.preventDefault();
            onTabChange('brokers');
            secondTabRef.current?.focus();
        }
    };

    return (
        <TabsContainer
            sx={{
                flexShrink: 0,
                position: 'absolute',
                bottom: 0,
                width: '100%',
                boxSizing: 'border-box',
            }}
            role="tablist"
            aria-label="location tabs"
            onKeyDown={handleTabsKeyDown}
        >
            <TabButton
                ref={firstTabRef}
                type="button"
                role="tab"
                aria-selected={activeTab === 'us-offices'}
                aria-controls="panel-us-offices"
                tabIndex={activeTab === 'us-offices' ? 0 : -1}
                selected={activeTab === 'us-offices'}
                onClick={() => handleTabClick('us-offices')}
            >
                <TabInnerBox isSelected={activeTab === 'us-offices'}>
                    <Typography
                        variant="label5regular"
                        sx={{ fontWeight: fontWeight.medium, color: color.white[0], textAlign: 'center' }}
                    >
                        {t('tabs.usOffices')}
                    </Typography>
                </TabInnerBox>
            </TabButton>
            <TabButton
                ref={secondTabRef}
                type="button"
                role="tab"
                aria-selected={activeTab === 'brokers'}
                aria-controls="panel-brokers"
                tabIndex={activeTab === 'brokers' ? 0 : -1}
                selected={activeTab === 'brokers'}
                onClick={() => handleTabClick('brokers')}
            >
                <TabInnerBox isSelected={activeTab === 'brokers'}>
                    <Typography
                        variant="label5regular"
                        sx={{ fontWeight: fontWeight.medium, color: color.white[0], textAlign: 'center' }}
                    >
                        {t('tabs.authorizedBrokers')}
                    </Typography>
                </TabInnerBox>
            </TabButton>
        </TabsContainer>
    );
}

